import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { XMarkIcon, TrashIcon, ArrowDownOnSquareIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

const HISTORY_STORAGE_KEY = 'rebbud_project_history';

const HistoryPanel = ({ onClose, onLoad, onDelete }) => {
    const [history, setHistory] = useState([]);
    const { t } = useTranslation();

    useEffect(() => {
        try {
            const storedHistory = JSON.parse(localStorage.getItem(HISTORY_STORAGE_KEY) || '[]');
            setHistory(storedHistory);
        } catch (error) {
            console.error("Failed to load project history from localStorage", error);
            setHistory([]);
        }
    }, []);

    const handleDeleteClick = useCallback((id) => {
        onDelete(id);
        setHistory(prev => prev.filter(item => item.id !== id));
    }, [onDelete]);

    const panelVariants = {
        hidden: { opacity: 0, x: -100 },
        visible: { opacity: 1, x: 0, transition: { type: "spring", stiffness: 300, damping: 30 } },
        exit: { opacity: 0, x: -100, transition: { duration: 0.2 } },
    };

    const formatDate = (timestamp) => {
        return new Date(timestamp).toLocaleString('uk-UA', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <motion.div
            key="history-panel"
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="absolute top-0 left-0 h-full w-80 bg-card-bg-light border-r border-gray-300 shadow-xl z-20 flex flex-col"
        >
            <div className="p-4 flex justify-between items-center border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg font-semibold text-gray-800">{t('history.title')}</h2>
                <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-800 p-1 rounded-full transition-colors"
                    title={t('toolbar.ai_panel_close')}
                >
                    <XMarkIcon className="w-6 h-6" />
                </button>
            </div>

            <div className="overflow-y-auto p-2">
                {history.length === 0 ? (
                    <div className="text-center p-8 text-gray-500">
                        <p>{t('history.empty')}</p>
                    </div>
                ) : (
                    <ul className="space-y-2">
                        {history.map((item) => (
                            <li key={item.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3 group hover:bg-gray-100 transition-colors">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <p className="text-sm font-medium text-gray-700">{item.preview}</p>
                                        <p className="text-xs text-gray-500 mt-1">{formatDate(item.timestamp)}</p>
                                    </div>
                                    <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => onLoad(item.id)} title={t('history.load')} className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-md">
                                            <ArrowDownOnSquareIcon className="w-5 h-5" />
                                        </button>
                                        <button onClick={() => handleDeleteClick(item.id)} title={t('history.delete')} className="p-1.5 text-red-600 hover:bg-red-100 rounded-md">
                                            <TrashIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </motion.div>
    );
};

export default HistoryPanel;